import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import CloseOutlined from "@mui/icons-material/CloseOutlined";

import {
  Checkbox,
  Dialog,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";

import "./AddEditWhatsappTemplate.css";
import Preview from "./Components/Preview";
import Steppers from "./Components/Steppers";
import { getAllAccount } from "../../Store/Actions/TicketAction";

const AddEditWhatsappTemplate = ({
  action,
  templateDetail,
  setTemplateDetail,
  componentsValues,
  updateComponentsValues,
  handleSubmit,
  isSubmitting,
}) => {
  // For validation when editing. Submit button will be disabled if there are no changes in the template
  const [isTemplateChanged, setIsTemplateChanged] = useState(false);

  const [isAdditionalInfoDialogShown, setIsAdditionalInfoDialogShown] =
    useState(false);

  const [isRejectWarningShown, setIsRejectWarningShown] = useState(false);
  const [isNameTooLong, setIsNameTooLong] = useState(false);
  const [WABAAccounts, setWABAAccounts] = useState([]);
  const { myInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    if (action === "edit" && templateDetail.status === "REJECTED") {
      setIsRejectWarningShown(true);
    }
  }, [action]);

  useEffect(() => {
    // Get agent data for search select options
    getAllAccount(myInfo.user.AgentID)
      .then((response) => {
        if (response.status) {
          setWABAAccounts(response.data.waba);
        } else {
          toast.error("Error fetching accounts", {
            autoClose: 3000,
          });

          console.log("error", response.error);
        }
      })
      .catch((err) => {
        toast.error("Error fetching accounts", {
          autoClose: 3000,
        });

        console.log("err", err);
      });
  }, []);

  const validateName = (name) => {
    if (name.length > 512) {
      setIsNameTooLong(true);
    } else {
      setIsNameTooLong(false);
    }
  };

  return (
    <div className="add-edit-whatsapp-template">
      <div className="container">
        <header>
          <p className="section-name">Template Detail</p>

          <Tooltip arrow placement="left" title="Show additional information">
            <IconButton
              className="information-button"
              size="small"
              onClick={() => setIsAdditionalInfoDialogShown(true)}
            >
              <Icon height="24" icon="mdi:information-outline" />
            </IconButton>
          </Tooltip>
        </header>

        <div className="form-container">
          <div className="row">
            <div style={{ flexBasis: "33.333333%" }}>
              <TextField
                error={isNameTooLong}
                fullWidth
                InputProps={{ readOnly: action === "edit" }}
                label="Name"
                placeholder="Input Name"
                required
                value={templateDetail.name}
                // onChange={(e) => {
                //   validateName(e.target.value);

                //   setTemplateDetail({
                //     ...templateDetail,

                //     // Replace spaces with underscore, and format to lowercase
                //     name: e.target.value.replace(" ", "_").toLowerCase(),
                //   });
                // }}

                onChange={(e) => {
                  const name = e.target.value
                    .replace(/[^a-zA-Z0-9 _]/g, "") // Only accepts alphanumerics, spaces, and underscores
                    .replace(/ /g, "_") // replace spaces with underscores
                    .toLowerCase();

                  validateName(name);

                  setTemplateDetail({
                    ...templateDetail,
                    name,
                  });
                }}
              />

              {isNameTooLong && (
                <p className="validation-error">
                  Template name should not be any longer than 512 characters
                </p>
              )}
            </div>

            <TextField
              InputProps={{ readOnly: action === "edit" }}
              label="Language"
              required
              select
              value={templateDetail.language}
              onChange={(e) =>
                setTemplateDetail({
                  ...templateDetail,
                  language: e.target.value,
                })
              }
            >
              <MenuItem value="id">Indonesian</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </TextField>

            <TextField
              InputProps={{
                readOnly:
                  // Meta limitation: You cannot edit the category of an approved template.
                  action === "edit" && templateDetail.status === "APPROVED",
              }}
              label="Category"
              required
              select
              value={templateDetail.category}
              onChange={(e) => {
                setIsTemplateChanged(true);

                setTemplateDetail({
                  ...templateDetail,
                  category: e.target.value,
                });
              }}
            >
              <MenuItem value="MARKETING">MARKETING</MenuItem>
              <MenuItem value="UTILITY">UTILITY</MenuItem>
            </TextField>
          </div>

          <div className="row">
            <TextField
              InputProps={{
                readOnly: action === "edit",
              }}
              label="WABA Account"
              required
              select
              value={templateDetail.WABAAccountID}
              onChange={(e) => {
                setIsTemplateChanged(true);

                setTemplateDetail({
                  ...templateDetail,
                  WABAAccountID: e.target.value,
                });
              }}
            >
              {WABAAccounts?.map((account) => (
                <MenuItem value={account.ID}>{account.Name}</MenuItem>
              ))}
            </TextField>

            <TextField
              InputProps={{ readOnly: action === "edit" }}
              label="Variable Type"
              required
              select
              value={templateDetail.parameterFormat}
              onChange={(e) =>
                setTemplateDetail({
                  ...templateDetail,
                  parameterFormat: e.target.value,
                })
              }
            >
              <MenuItem value="named">Name</MenuItem>
              <MenuItem value="positional">Number</MenuItem>
            </TextField>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    color="default"
                    checked={templateDetail.isCallPermission}
                    disabled={action === "edit"}
                    onChange={(event) =>
                      setTemplateDetail({
                        ...templateDetail,
                        isCallPermission: event.target.checked,
                      })
                    }
                  />
                }
                label="Call Request"
              />
            </FormGroup>
          </div>

          {action === "edit" && (
            <div className="row">
              <TextField
                className={
                  templateDetail.status === "REJECTED"
                    ? "rejected"
                    : templateDetail.status === "APPROVED"
                    ? "approved"
                    : "pending"
                }
                inputProps={{ readOnly: true }}
                InputProps={{
                  endAdornment: templateDetail.status === "REJECTED" && (
                    <IconButton
                      size="small"
                      onClick={() => setIsRejectWarningShown(true)}
                    >
                      <Icon
                        color="#E74C3C"
                        height="26"
                        icon="mingcute:warning-fill"
                      />
                    </IconButton>
                  ),
                }}
                label="Status"
                value={templateDetail.status}
              />

              <TextField
                inputProps={{ readOnly: true }}
                label="Status Time"
                value={templateDetail.statusTime}
              />

              <div className="button-container">
                <button className="delete-button" disabled>
                  <Icon
                    height="20"
                    icon="material-symbols:delete-outline-rounded"
                  />
                  Delete
                </button>

                {/* <button
                  className="submit-button"
                  disabled={templateDetail.status === "Rejected"}
                  onClick={handleSubmit}
                >
                  Submit Template
                </button> */}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="container">
        <p className="section-name">Components</p>

        <div className="components-content">
          <Steppers
            action={action}
            componentsValues={componentsValues}
            updateComponentsValues={updateComponentsValues}
            templateDetail={templateDetail}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isTemplateChanged={isTemplateChanged}
            setIsTemplateChanged={setIsTemplateChanged}
          />

          <Preview
            componentsValues={componentsValues}
            isCallPermission={templateDetail.isCallPermission}
            parameterFormat={templateDetail.parameterFormat}
          />
        </div>
      </div>

      <Dialog
        open={isAdditionalInfoDialogShown}
        className="dialog additional-info"
        scroll="paper"
        onClose={() => setIsAdditionalInfoDialogShown(false)}
      >
        <div className="dialog-container">
          <div className="dialog-content">
            <IconButton
              className="close-button"
              onClick={() => setIsAdditionalInfoDialogShown(false)}
            >
              <CloseOutlined />
            </IconButton>

            <div className="title">
              <h2>Information</h2>
            </div>

            <section>
              <p>Whatsapp Template Limitations</p>

              <ul>
                <li>
                  Only templates with an APPROVED, REJECTED, or PAUSED status
                  can be edited.
                </li>

                <li>You can only edit a template's category or components.</li>
                <li>You cannot edit the category of an approved template.</li>

                <li>
                  Approved templates can be edited up to 10 times in a 30 day
                  window, or 1 time in a 24 hour window. Rejected or paused
                  templates can be edited an unlimited number of times.
                </li>

                <li>
                  After editing an approved or paused template, it will
                  automatically be approved unless it fails template review.
                </li>
              </ul>
            </section>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={isRejectWarningShown}
        className="dialog rejected-template-warning"
        fullWidth
        maxWidth="sm"
        scroll="paper"
        onClose={() => setIsRejectWarningShown(false)}
      >
        <div className="dialog-container">
          <div className="dialog-warning">
            <header>
              <Icon height="28" icon="ion:warning" width="28" />
              <p>Warning!</p>
              <Icon height="28" icon="ion:warning" width="28" />
            </header>

            <div className="warning-content">
              <p>This Template has been rejected by Meta</p>
            </div>

            <div className="buttons">
              <button
                className="confirm"
                onClick={() => setIsRejectWarningShown(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default AddEditWhatsappTemplate;
